import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Keep data in cache for 30 minutes
      gcTime: 30 * 60 * 1000,

      // Retry failed requests once
      retry: 1,

      // Don't refetch on window focus in development
      refetchOnWindowFocus: import.meta.env.PROD,

      // Refetch when reconnecting
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Events
  events: {
    all: ['events'] as const,
    upcoming: (params?: unknown) => ['events', 'upcoming', params] as const,
    past: (params?: unknown) => ['events', 'past', params] as const,
    detail: (year: number, slug: string) => ['events', 'detail', year, slug] as const,
    pastDetail: (year: number, slug: string) => ['events', 'pastDetail', year, slug] as const,
    types: ['events', 'types'] as const,
    options: (eventId: string) => ['events', eventId, 'options'] as const,
    components: (eventId: string) => ['events', eventId, 'components'] as const,
  },

  // Articles
  articles: {
    all: ['articles'] as const,
    list: (params?: unknown) => ['articles', 'list', params] as const,
    detail: (categorySlug: string, slug: string) => ['articles', categorySlug, slug] as const,
    categories: ['articles', 'categories'] as const,
    latest: (count: number) => ['articles', 'latest', count] as const,
  },

  // Sponsors
  sponsors: {
    all: ['sponsors'] as const,
    list: ['sponsors', 'list'] as const,
    detail: (slug: string) => ['sponsors', slug] as const,
    types: ['sponsors', 'types'] as const,
  },

  // Members
  members: {
    dashboard: ['members', 'dashboard'] as const,
    profile: ['members', 'profile'] as const,
    emergencyContact: ['members', 'emergencyContact'] as const,
    testimonial: ['members', 'testimonial'] as const,
    qrcode: ['members', 'qrcode'] as const,
  },

  // Vehicles
  vehicles: {
    all: ['vehicles'] as const,
    list: ['vehicles', 'list'] as const,
    detail: (id: number) => ['vehicles', id] as const,
    lookup: (registration: string) => ['vehicles', 'lookup', registration] as const,
  },

  // Polls
  polls: {
    all: ['polls'] as const,
    active: ['polls', 'active'] as const,
    completed: ['polls', 'completed'] as const,
    detail: (id: number) => ['polls', id] as const,
    results: (id: number) => ['polls', id, 'results'] as const,
  },

  // Credits
  credits: {
    balance: ['credits', 'balance'] as const,
    history: ['credits', 'history'] as const,
    opportunities: ['credits', 'opportunities'] as const,
  },

  // Competitions
  competitions: {
    all: ['competitions'] as const,
    active: ['competitions', 'active'] as const,
    detail: (url: string) => ['competitions', url] as const,
    leaderboard: (id: string) => ['competitions', id, 'leaderboard'] as const,
  },

  // Bookings
  bookings: {
    all: ['bookings'] as const,
    upcoming: ['bookings', 'upcoming'] as const,
    past: ['bookings', 'past'] as const,
    detail: (id: string) => ['bookings', id] as const,
  },

  // Photos
  photos: {
    all: ['photos'] as const,
    event: (eventId: string, params?: unknown) => ['photos', 'event', eventId, params] as const,
    member: (memberId: string, params?: unknown) => ['photos', 'member', memberId, params] as const,
    my: (params?: unknown) => ['photos', 'my', params] as const,
  },

  // Passengers - memberId is 'me' for the member area, or a member GUID for admin
  passengers: {
    all: (memberId: string) => ['passengers', memberId] as const,
    detail: (memberId: string, passengerId: string) => ['passengers', memberId, passengerId] as const,
  },

  // Event booking consent (PCE-396/397)
  consent: {
    detail: (eventId: string, bookingId: string) => ['consent', eventId, bookingId] as const,
  },
};
